import type { Locale } from './i18n';

/*
 * The single methodology registry. Every "how to read this" caveat that the
 * legacy dashboard repeated across ~6–10 places per screen lives here ONCE.
 * Surfaced through exactly three channels: the MethodologySheet (one button in
 * the FilterBar), an inline <CaveatRef id> next to the exact number it qualifies,
 * and self-explaining empty states. nb + en, authored from the legacy copy.
 */

export type MethodologyId =
  | 'colorLegend'
  | 'blendedAttribution'
  | 'showupDating'
  | 'showupBlendedRate'
  | 'revenueEqualsCash'
  | 'roiFirstMonth'
  | 'segmentHardStop'
  | 'leadHandoff'
  | 'bookingIsMeta'
  | 'trustMeta'
  | 'rulesWindowBlended'
  | 'planDirectional'
  | 'backfillSur'
  | 'clarityGap'
  | 'bookingNameMatch';

interface Entry {
  title: Record<Locale, string>;
  body: Record<Locale, string>;
}

export const METHODOLOGY: Record<MethodologyId, Entry> = {
  colorLegend: {
    title: { nb: 'Fargene', en: 'The colors' },
    body: {
      nb: 'Bare tall med et aktivt mål får farge. Grønn = på mål, gul = innen 10 % av målet, rød = mer enn 10 % unna. Retningen (høyere/lavere er bedre) følger målet.',
      en: 'Only metrics with an active target get color. Green = on target, amber = within 10% of it, red = more than 10% off. Direction (higher/lower is better) follows the target.',
    },
  },
  blendedAttribution: {
    title: { nb: 'Blandet attribusjon', en: 'Blended attribution' },
    body: {
      nb: 'Ratene teller ALLE bookinger og salg i studioet, ikke bare de annonse-drevne. Reell annonse-CPA er høyere — Meta Ads Manager er fasit for annonsekreditering.',
      en: 'Rates count ALL gym bookings and sales, not just ad-sourced ones. True ad-only CPA is higher — Meta Ads Manager is the source of truth for ad credit.',
    },
  },
  showupDating: {
    title: { nb: 'Oppmøte dateres til timedagen', en: 'Show-up is dated to the appointment day' },
    body: {
      nb: 'Vi leser hver times innsjekk-bryter i Momence og teller oppmøtet på timens dato. Forutsetter at staben huker av oppmøte i Momence.',
      en: "We read each appointment's check-in toggle in Momence and count the show-up on the appointment day. Depends on staff toggling check-in in Momence.",
    },
  },
  showupBlendedRate: {
    title: { nb: 'Oppmøterate er blandet', en: 'Show-up rate is blended' },
    body: {
      nb: 'Oppmøte her er den blandede raten oppmøtt÷bookinger. Den ekte person-baserte raten er ~37,5 % (bookinger er linjeposter, oppmøter er personer). Vi bruker den blandede raten så modellen stemmer med tallene dine.',
      en: 'Show-up here is the blended showedUp÷bookings rate. The true person-based rate is ~37.5% (bookings are line-items, show-ups are people). We use the blended rate so the model reconciles with your numbers.',
    },
  },
  revenueEqualsCash: {
    title: { nb: 'Omsetning ≈ kontant', en: 'Revenue ≈ cash' },
    body: {
      nb: 'Medlemskap faktureres via Stripe ved salg, så omsetning og kontant er nesten like — ikke to uavhengige kilder.',
      en: 'Memberships bill through Stripe at sale time, so revenue and cash are nearly equal — not two independent sources.',
    },
  },
  roiFirstMonth: {
    title: { nb: 'ROI er første måned', en: 'ROI is first-month' },
    body: {
      nb: 'ROI er blandet og for første måned — medlemskap er gjentakende, så livstidsverdien er langt høyere. Drep aldri annonsebudsjett på første-måneds-ROI alene.',
      en: 'ROI is blended and first-month — memberships recur, so lifetime value is far higher. Never kill spend on first-month ROI alone.',
    },
  },
  segmentHardStop: {
    title: { nb: 'To ulike funneler', en: 'Two different funnels' },
    body: {
      nb: 'Booking og Lead er to funneler. Segment-bryteren deler dem KUN på Meta-siden (forbruk, klikk, leads/CPL, bookinger/kost-per-booking). Oppmøte, salg, omsetning og ROI er en delt studio-pool — se Alle for full P/L.',
      en: 'Booking and Lead are two funnels. The segment toggle splits them on the Meta side ONLY (spend, clicks, leads/CPL, bookings/cost-per-booking). Show-up, sales, revenue & ROI are a shared gym pool — see All for the full P&L.',
    },
  },
  leadHandoff: {
    title: { nb: 'Lead → offline oppfølging', en: 'Lead → offline nurture' },
    body: {
      nb: 'Leads overleveres til offline oppfølging (ring/SMS). Trial → medlem fra disse leadsene er ikke tagget tilbake til kampanjen ennå — se Alle.',
      en: "Leads are handed to offline nurture (call/SMS). Trial → member from these leads isn't tagged back to the campaign yet — see All.",
    },
  },
  bookingIsMeta: {
    title: { nb: 'Bookinger = Meta', en: 'Bookings = Meta' },
    body: {
      nb: 'I Booking-funnelen er «Bookinger» Metas conversions.schedule_website (matcher Ads Manager). I Alle-visningen er bookinger Momence-timer (hele studioet, inkl. drop-in).',
      en: "In the Booking funnel, “Bookings” is Meta's conversions.schedule_website (matches Ads Manager). In the All view, bookings are Momence appointments (gym-wide, incl. walk-ins).",
    },
  },
  trustMeta: {
    title: { nb: 'Stol på Meta for kreditering', en: 'Trust Meta for ad credit' },
    body: {
      nb: 'Våre interne tall over-teller annonse-bookinger (heuristisk matching). Meta validerer selv og krediterer kun ekte treff — stol på Ads Manager-tallene for reell attribusjon.',
      en: 'Our internal counts over-count ad bookings (heuristic matching). Meta validates and credits only genuine matches — trust Ads Manager for true attribution.',
    },
  },
  rulesWindowBlended: {
    title: { nb: 'Varsler er blandet over vinduet', en: 'Alerts are blended over the window' },
    body: {
      nb: 'Observert verdi er SUM(teller)/SUM(nevner) over regelens vindu — ikke gjennomsnitt av daglige rater. Kritiske varsler pinger Slack én gang per dag.',
      en: "Observed value is SUM(numerator)/SUM(denominator) over the rule's window — not a mean of daily rates. Critical alerts ping Slack once per day.",
    },
  },
  planDirectional: {
    title: { nb: 'Planen er retningsgivende', en: 'The plan is directional' },
    body: {
      nb: 'Bygget på et lite utvalg fra dine reelle rater. Behandle hvert tall som et retningsgivende spenn, ikke en prognose. Skalering er ikke-lineær — øk budsjett ≤20 % hver 3.–4. dag.',
      en: 'Built on a small sample from your real rates. Treat every output as a directional range, not a forecast. Scaling is non-linear — ramp budgets ≤20% every 3–4 days.',
    },
  },
  backfillSur: {
    title: { nb: 'Backfill-klump i oppmøte', en: 'Backfill lump in show-up' },
    body: {
      nb: 'Dette vinduet inkluderer en backfill av oppmøte — oppmøtetall er ekstra usikre. Velg et vindu etter backfill-datoen for renere oppmøterate.',
      en: 'This window includes a show-up backfill — show-up figures are extra uncertain. Pick a window after the backfill date for a cleaner show-up rate.',
    },
  },
  clarityGap: {
    title: { nb: 'Clarity vs. Meta-booking', en: 'Clarity vs. Meta booking' },
    body: {
      nb: 'Clarity teller «Book»-hendelser (åpning av booking-modalen) — finn-CTA-steget. Den reelle konverteringen er Meta schedule_website. Gapet = folk som åpner modalen, men ikke fullfører i Momence-iframen.',
      en: 'Clarity counts "Book" events (booking-modal opens) — the find-the-CTA step. The real conversion is Meta schedule_website. The gap = people who open the modal but don\'t complete in the Momence iframe.',
    },
  },
  bookingNameMatch: {
    title: { nb: 'Meta-tall vs. navngitte bookinger', en: 'Meta count vs. named bookings' },
    body: {
      nb: 'Meta validerer konverteringer mot sin egen brukergraf — vi ser ikke hvem de krediterte. Vår navngivning parer bookingen med siste uleste annonseklikk (last-click-heuristikk), og ad_id finnes bare for klikk etter at annonse-URL-ene ble tagget. Tallene kan derfor avvike begge veier.',
      en: "Meta validates conversions against its own user graph — we can't see whom it credited. Our naming pairs the booking with the most recent unclaimed ad click (a last-click heuristic), and ad_id only exists for clicks after the ad URLs were tagged. The two counts can therefore differ in either direction.",
    },
  },
};

export const METHODOLOGY_ORDER: MethodologyId[] = [
  'colorLegend',
  'segmentHardStop',
  'blendedAttribution',
  'trustMeta',
  'showupDating',
  'showupBlendedRate',
  'revenueEqualsCash',
  'roiFirstMonth',
  'leadHandoff',
  'bookingIsMeta',
  'rulesWindowBlended',
  'planDirectional',
  'backfillSur',
];
